import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, signal, WritableSignal } from '@angular/core';
import { form, FormField, min, required } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PropertyType } from '@workspace/data-types';
import { AbstractSignalFormComponent } from '@workspace/ng-aura';

export interface PropertySurfaceComponentDto {
  habitableSurface?: number;
  groundSurface?: number;
  landSurface?: number;
  cadastralSurface?: number;
  propertyType?: PropertyType;
}

export interface PropertySurfaceModel {
  habitableSurface: number | null;
  groundSurface: number | null;
  landSurface: number | null;
}

@Component({
  selector: 'etxe-property-surface',
  imports: [
    DecimalPipe,
    FormField,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
  ],
  templateUrl: './surface.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertySurfaceComponent extends AbstractSignalFormComponent<
  PropertySurfaceComponentDto,
  PropertySurfaceModel
> {
  readonly isHouse = computed(() => this.dto()?.propertyType === PropertyType.HOUSE);
  sellingPriceCents = input<number | undefined>();

  cadastralSurface = computed(() => this.dto()?.cadastralSurface);

  isLandSurfaceModifiedFromCadastre = computed(() => {
    const cad = this.cadastralSurface();
    const current = this.model().landSurface;
    return cad != null && current != null && cad !== current;
  });

  pricePerSquareMeter = computed(() => {
    const price = this.sellingPriceCents();
    const surface = this.model().habitableSurface;
    if (!price || !surface || surface <= 0) return null;
    return Math.round(price / 100 / surface);
  });

  resetToCadastralSurface(): void {
    const cad = this.cadastralSurface();
    if (cad != null) {
      this.model.update((m) => ({ ...m, landSurface: cad }));
    }
  }

  override model: WritableSignal<PropertySurfaceModel> = signal<PropertySurfaceModel>({
    habitableSurface: null,
    groundSurface: null,
    landSurface: null,
  });

  override formTree = form(this.model, (b) => {
    required(b.habitableSurface);
    required(b.groundSurface);
    min(b.habitableSurface, 9);
    min(b.groundSurface, () => this.model().habitableSurface ?? 0);
    required(b.landSurface, { when: () => this.isHouse() });
    min(b.landSurface, 1, { when: () => this.isHouse() });
  });

  protected override buildModelFromDto(dto: PropertySurfaceComponentDto): PropertySurfaceModel {
    return {
      habitableSurface: dto?.habitableSurface ?? null,
      groundSurface: dto?.groundSurface ?? null,
      landSurface: dto?.landSurface ?? dto?.cadastralSurface ?? null,
    };
  }

  protected override buildDtoFromModel(model: PropertySurfaceModel): PropertySurfaceComponentDto {
    return {
      habitableSurface: model.habitableSurface ?? undefined,
      groundSurface: model.groundSurface ?? undefined,
      landSurface: model.landSurface ?? undefined,
      cadastralSurface: this.dto()?.cadastralSurface,
      propertyType: this.dto()?.propertyType,
    };
  }
}
